import { IRecruiterDashboardMetrics } from '../../../../../hooks/useRecruiterDashboardMetrics';
import { IRecruiterDashboardSettings } from '../../../../../hooks/useRecruiterDashboardSettings';
import {
  CHURN_RATE_BREAKPOINTS,
  METRIC_HIGHER_IS_BETTER,
} from '../../../constants';
import { IChurnDetailsData, IDateBounds, IModalLabels } from '../../types';
import { getThresholdStatus, IThresholdStatus } from '../getThresholdStatus';

export interface IChurnModalData {
  churnPeriodData: IChurnDetailsData | null;
  churnQuarterlyData: IChurnDetailsData | null;
  periodChurnStatus: IThresholdStatus;
  quarterlyChurnStatus: IThresholdStatus;
}

function buildChurnEntry(
  periodLabel: string,
  periodStart: Date,
  periodEnd: Date,
  raw: {
    churnRate: number;
    dropoutCount: number;
    enrollmentCount: number;
    activeAtStart: number;
  },
  status: IThresholdStatus,
  breakpoints: [number, number],
  breakpointLabels: { low: string; mid: string; high: string }
): IChurnDetailsData {
  return {
    periodLabel,
    periodStart,
    periodEnd,
    ...raw,
    statusLabel: status.label,
    statusColor: status.color,
    breakpoints,
    breakpointLabels,
  };
}

export function buildChurnModalData(
  metrics: IRecruiterDashboardMetrics | null,
  settings: IRecruiterDashboardSettings | null,
  dateBounds: IDateBounds,
  quarterlyDateBounds: IDateBounds,
  labels: IModalLabels
): IChurnModalData {
  const breakpoints = settings?.churnRateBreakpoints ?? CHURN_RATE_BREAKPOINTS;
  const breakpointLabels = settings?.churnRateLabels ?? {
    low: labels.statusGood,
    mid: labels.statusOk,
    high: labels.statusPoor,
  };

  const periodChurnStatus = getThresholdStatus(
    metrics?.periodChurnRate ?? 0,
    breakpoints,
    breakpointLabels,
    METRIC_HIGHER_IS_BETTER.churn
  );
  const quarterlyChurnStatus = getThresholdStatus(
    metrics?.quarterlyChurnRate ?? 0,
    breakpoints,
    breakpointLabels,
    METRIC_HIGHER_IS_BETTER.churn
  );

  return {
    periodChurnStatus,
    quarterlyChurnStatus,
    churnPeriodData: metrics
      ? buildChurnEntry(
          labels.currentPeriod,
          dateBounds.start,
          dateBounds.end,
          {
            churnRate: metrics.periodChurnRate,
            dropoutCount: metrics.periodDropoutCount,
            enrollmentCount: metrics.periodNewEnrollmentCount,
            activeAtStart: metrics.periodActiveAtStart,
          },
          periodChurnStatus,
          breakpoints,
          breakpointLabels
        )
      : null,
    churnQuarterlyData: metrics
      ? buildChurnEntry(
          labels.thisQuarter,
          quarterlyDateBounds.start,
          quarterlyDateBounds.end,
          {
            churnRate: metrics.quarterlyChurnRate,
            dropoutCount: metrics.quarterlyDropoutCount,
            enrollmentCount: metrics.quarterlyNewEnrollmentCount,
            activeAtStart: metrics.quarterlyActiveAtStart,
          },
          quarterlyChurnStatus,
          breakpoints,
          breakpointLabels
        )
      : null,
  };
}
